
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";

type InvitationAcceptProps = {
  token: string;
};

const InvitationAccept = ({ token }: InvitationAcceptProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const acceptInvitation = async () => {
    if (!token || !user) return;

    setIsLoading(true);
    try {
      // Retrouver l'invitation par son ID (token)
      const { data: invitation, error: invitationError } = await supabase
        .from('organization_invitations')
        .select('*')
        .eq('id', token)
        .single();

      if (invitationError) throw invitationError;

      if (!invitation) {
        toast({
          title: "Invitation invalide",
          description: "L'invitation que vous essayez d'accepter n'existe pas ou a expiré.",
          variant: "destructive",
        });
        return;
      }

      // Ajouter l'utilisateur comme membre de l'organisation
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: invitation.organization_id,
          user_id: user.id,
          role: invitation.role || 'member'
        });

      if (memberError) throw memberError;

      // Mettre à jour le statut de l'invitation
      const { error: updateError } = await supabase
        .from('organization_invitations')
        .update({ status: 'accepted' })
        .eq('id', token);

      if (updateError) throw updateError;

      // Incrémenter le compteur d'utilisateurs de l'organisation
      const { error: orgUpdateError } = await supabase
        .from('organizations')
        .update({ current_users: supabase.rpc('increment', { inc_amount: 1 }) })
        .eq('id', invitation.organization_id);

      if (orgUpdateError) throw orgUpdateError;

      toast({
        title: "Invitation acceptée",
        description: "Vous avez rejoint l'organisation avec succès.",
        variant: "default",
      });

      // Rediriger vers le tableau de bord
      navigate('/dashboard');
    } catch (error) {
      console.error("Erreur lors de l'acceptation de l'invitation:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'acceptation de l'invitation.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-12 px-4 max-w-md">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Invitation à rejoindre une organisation</h1>
        <p className="text-gray-600 mb-6">
          Vous avez été invité à rejoindre une organisation. Cliquez sur le bouton ci-dessous pour accepter l'invitation.
        </p>
        <Button
          onClick={acceptInvitation}
          className="bg-blue-500 hover:bg-blue-600 text-white w-full"
          disabled={isLoading}
        >
          {isLoading ? "Traitement en cours..." : "Accepter l'invitation"}
        </Button>
      </div>
    </div>
  );
};

export default InvitationAccept;
